import React, { FC, PropsWithChildren, ReactNode, useEffect, useState } from "react";
import {
  Box,
  Text,
  TabsContent,
  TabsContentGroup,
  TabsList,
  TabsRoot,
  TabsTrigger,
  type TabsValueChangeDetails,
} from "@chakra-ui/react";
import { TabType } from "./types";
import { useTheme } from "@emotion/react";

const TabsListRow = TabsList as React.FC<React.PropsWithChildren<Record<string, unknown>>>;
const TabsTriggerButton = TabsTrigger as React.FC<
  React.PropsWithChildren<Record<string, unknown>>
>;

type TabsProps = {
  title?: string;
  tabs: TabType[];
  defaultTab?: number;
  currentTab?: number;
  handleTabsChange?: (currentTab: number) => void;
};

export const Tabs: FC<PropsWithChildren<TabsProps>> = ({
  title,
  tabs,
  children,
  defaultTab,
  currentTab,
  handleTabsChange,
}) => {
  const theme = useTheme();
  const [internalIndex, setInternalIndex] = useState(defaultTab ?? 0);

  useEffect(() => {
    if (typeof currentTab === "number") {
      return;
    }
    if (!tabs?.length) {
      return;
    }
    setInternalIndex((prev) => {
      if (prev < 0 || prev >= tabs.length) {
        return 0;
      }
      return prev;
    });
  }, [tabs?.length, currentTab]);

  const onTabChange = (nextIndex: number) => {
    if (typeof currentTab !== "number") {
      setInternalIndex(nextIndex);
    }
    handleTabsChange?.(nextIndex);
  };

  const idx = typeof currentTab === "number" ? currentTab : internalIndex;
  const value = String(idx);

  return (
    <Box
      border={`2px solid ${theme.colors.borders.light}`}
      borderTop={`8px solid ${theme.colors.borders.strong}`}
      borderRadius="10"
      bg={theme.colors.backgrounds.primary}
    >
      <Text p="4" fontWeight="bold">
        {title}
      </Text>
      <TabsRoot
        value={value}
        onValueChange={(e: TabsValueChangeDetails) =>
          onTabChange(Number(e.value))
        }
      >
        <TabsListRow borderBottom="none">
          {tabs?.map((tab: TabType, i: number) => (
            <TabsTriggerButton
              key={tab.label}
              value={String(i)}
              color={theme.colors.text.quaternary}
              fontWeight="bold"
              px="0"
              mx="4"
              _selected={{
                color: "black",
                borderBottom: `3px solid black`,
              }}
            >
              {tab.label}
              {tab.required && " *"}
              {tab.icon}
            </TabsTriggerButton>
          ))}
        </TabsListRow>
        <TabsContentGroup padding="1rem">{children}</TabsContentGroup>
      </TabsRoot>
    </Box>
  );
};

/** Bridge: Chakra v3 `TabsContent` typings omit `children` in strict TS. */
const TabsContentBox = TabsContent as React.FC<
  React.PropsWithChildren<{ value: string }>
>;

export const TabsPanel = ({
  value,
  children,
}: {
  value: string;
  children: ReactNode;
}) => <TabsContentBox value={value}>{children}</TabsContentBox>;
